import { useState, useEffect, useRef } from "react";
import { Table, Button, Modal, Form, Alert, Row, Col, Badge } from "react-bootstrap";
import Api from "../../../Services/Api";

interface Medication {
  id: number;
  name: string;
  presentation?: string;
  createdAt?: string;
  updatedAt?: string;
}

interface MedicationActiveSubstance {
  id: number;
  medicationId: number;
  activeSubstanceId: number;
  activeSubstanceUnitId: number;
  quantity: number;
  medication?: {
    id: number;
    name: string;
  };
  activeSubstance?: {
    id: number;
    name: string;
  };
  activeSubstanceUnit?: {
    id: number;
    name: string;
    abbreviation: string;
  };
  createdAt?: string;
  updatedAt?: string;
}

const Medications = () => {
  const [medications, setMedications] = useState<Medication[]>([]);
  const [medicationActiveSubstances, setMedicationActiveSubstances] = useState<MedicationActiveSubstance[]>([]);
  const [activeSubstanceUnits, setActiveSubstanceUnits] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [showMedicationModal, setShowMedicationModal] = useState(false);
  const [editingMedication, setEditingMedication] = useState<Medication | null>(null);
  const [editingMedicationActiveSubstance, setEditingMedicationActiveSubstance] = useState<MedicationActiveSubstance | null>(null);
  const [error, setError] = useState<string>("");
  const [success, setSuccess] = useState<string>("");
  
  // Debounce timer for active substance search
  const activeSubstanceSearchDebounceTimer = useRef<NodeJS.Timeout | null>(null);
  
  const [medicationFormData, setMedicationFormData] = useState({
    name: "",
    presentation: "",
  });
  
  const [activeSubstanceFormData, setActiveSubstanceFormData] = useState({
    medicationId: 0,
    activeSubstanceId: 0,
    activeSubstanceUnitId: 0,
    quantity: 0,
  });
  
  const [activeSubstanceSearchTerm, setActiveSubstanceSearchTerm] = useState<string>("");
  const [activeSubstanceSearchResults, setActiveSubstanceSearchResults] = useState<any[]>([]);
  const [showActiveSubstanceDropdown, setShowActiveSubstanceDropdown] = useState<boolean>(false);
  const [selectedActiveSubstance, setSelectedActiveSubstance] = useState<any | null>(null);
  const [showAddActiveSubstanceSection, setShowAddActiveSubstanceSection] = useState<boolean>(false);

  useEffect(() => {
    loadData();
    
    // Cleanup function to clear debounce timer
    return () => {
      if (activeSubstanceSearchDebounceTimer.current) {
        clearTimeout(activeSubstanceSearchDebounceTimer.current);
      }
    };
  }, []);
  
  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (showActiveSubstanceDropdown && !target.closest('[data-active-substance-search]')) {
        setShowActiveSubstanceDropdown(false);
      }
    };
    
    if (showMedicationModal && editingMedication) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [showActiveSubstanceDropdown, showMedicationModal, editingMedication]);

  const loadData = async () => {
    setLoading(true);
    const api = new Api();
    const [medicationsData, activeSubstancesData, unitsData] = await Promise.all([
      api.getMedications(),
      api.getMedicationActiveSubstances(),
      api.getActiveSubstanceUnits(),
    ]);
    if (medicationsData) {
      setMedications(medicationsData);
    }
    if (activeSubstancesData) {
      setMedicationActiveSubstances(activeSubstancesData);
    }
    if (unitsData) {
      setActiveSubstanceUnits(unitsData);
    }
    setLoading(false);
  };

  const getMedicationActiveSubstances = (medicationId: number): MedicationActiveSubstance[] => {
    return medicationActiveSubstances.filter(mas => mas.medicationId === medicationId);
  };

  const searchActiveSubstances = async (searchTerm: string) => {
    if (!searchTerm || searchTerm.trim().length === 0) {
      setActiveSubstanceSearchResults([]);
      setShowActiveSubstanceDropdown(false);
      return;
    }

    try {
      const api = new Api();
      const results = await api.searchActiveSubstances(searchTerm);
      if (results) {
        setActiveSubstanceSearchResults(results);
        setShowActiveSubstanceDropdown(true);
      }
    } catch (err) {
      setActiveSubstanceSearchResults([]);
      setShowActiveSubstanceDropdown(false);
    }
  };

  const handleActiveSubstanceSearchChange = (value: string) => {
    setActiveSubstanceSearchTerm(value);
    
    // If user starts typing after selection, clear the selection
    if (selectedActiveSubstance && value) {
      setSelectedActiveSubstance(null);
      setActiveSubstanceFormData({ ...activeSubstanceFormData, activeSubstanceId: 0 });
    }

    // Clear existing timer
    if (activeSubstanceSearchDebounceTimer.current) {
      clearTimeout(activeSubstanceSearchDebounceTimer.current);
    }

    // Set new timer
    activeSubstanceSearchDebounceTimer.current = setTimeout(() => {
      if (value.trim()) {
        searchActiveSubstances(value);
      } else {
        setActiveSubstanceSearchResults([]);
        setShowActiveSubstanceDropdown(false);
      }
    }, 1000);
  };

  const handleSelectActiveSubstance = (substance: any) => {
    setSelectedActiveSubstance(substance);
    setActiveSubstanceSearchTerm("");
    setActiveSubstanceFormData({ ...activeSubstanceFormData, activeSubstanceId: substance.id });
    setShowActiveSubstanceDropdown(false);
  };

  const handleClearActiveSubstance = () => {
    setSelectedActiveSubstance(null);
    setActiveSubstanceSearchTerm("");
    setActiveSubstanceFormData({ ...activeSubstanceFormData, activeSubstanceId: 0 });
    setActiveSubstanceSearchResults([]);
    setShowActiveSubstanceDropdown(false);
  };

  // Medication CRUD
  const handleOpenCreateMedication = () => {
    setEditingMedication(null);
    setMedicationFormData({ name: "", presentation: "" });
    setError("");
    setSuccess("");
    setShowMedicationModal(true);
  };

  const handleOpenEditMedication = (medication: Medication) => {
    setEditingMedication(medication);
    setMedicationFormData({
      name: medication.name,
      presentation: medication.presentation || "",
    });
    // Reset active substance form when opening edit
    setActiveSubstanceFormData({
      medicationId: medication.id,
      activeSubstanceId: 0,
      activeSubstanceUnitId: 0,
      quantity: 0,
    });
    setActiveSubstanceSearchTerm("");
    setSelectedActiveSubstance(null);
    setActiveSubstanceSearchResults([]);
    setShowActiveSubstanceDropdown(false);
    setEditingMedicationActiveSubstance(null);
    setError("");
    setSuccess("");
    setShowMedicationModal(true);
  };

  const handleCloseMedicationModal = () => {
    // Clear debounce timer
    if (activeSubstanceSearchDebounceTimer.current) {
      clearTimeout(activeSubstanceSearchDebounceTimer.current);
    }
    setShowMedicationModal(false);
    setEditingMedication(null);
    setEditingMedicationActiveSubstance(null);
    setMedicationFormData({ name: "", presentation: "" });
    setActiveSubstanceFormData({ medicationId: 0, activeSubstanceId: 0, activeSubstanceUnitId: 0, quantity: 0 });
    setActiveSubstanceSearchTerm("");
    setSelectedActiveSubstance(null);
    setActiveSubstanceSearchResults([]);
    setShowActiveSubstanceDropdown(false);
    setShowAddActiveSubstanceSection(false);
    setError("");
    setSuccess("");
  };

  const handleSubmitMedication = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    if (!medicationFormData.name) {
      setError("Please fill in all fields");
      return;
    }

    try {
      const api = new Api();
      if (editingMedication) {
        await api.updateMedication(editingMedication.id, medicationFormData);
        setSuccess("Medication updated successfully");
      } else {
        await api.createMedication(medicationFormData);
        setSuccess("Medication created successfully");
      }
      handleCloseMedicationModal();
      loadData();
    } catch (err: any) {
      setError(err.response?.data?.message || "An error occurred");
    }
  };

  const handleDeleteMedication = async (id: number) => {
    if (!window.confirm("Are you sure you want to delete this medication?")) {
      return;
    }

    try {
      const api = new Api();
      await api.deleteMedication(id);
      setSuccess("Medication deleted successfully");
      loadData();
    } catch (err: any) {
      setError(err.response?.data?.message || "An error occurred");
    }
  };

  // Medication Active Substance CRUD
  const handleOpenEditActiveSubstance = (mas: MedicationActiveSubstance) => {
    setEditingMedicationActiveSubstance(mas);
    setActiveSubstanceFormData({
      medicationId: mas.medicationId,
      activeSubstanceId: mas.activeSubstanceId,
      activeSubstanceUnitId: mas.activeSubstanceUnitId,
      quantity: mas.quantity,
    });
    if (mas.activeSubstance) {
      setSelectedActiveSubstance(mas.activeSubstance);
      setActiveSubstanceSearchTerm(mas.activeSubstance.name);
    } else {
      setSelectedActiveSubstance(null);
      setActiveSubstanceSearchTerm("");
    }
    setActiveSubstanceSearchResults([]);
    setShowActiveSubstanceDropdown(false);
    setShowAddActiveSubstanceSection(true);
    setError("");
    setSuccess("");
  };

  const handleCancelEditActiveSubstance = () => {
    setEditingMedicationActiveSubstance(null);
    setActiveSubstanceFormData({
      medicationId: editingMedication?.id || 0,
      activeSubstanceId: 0,
      activeSubstanceUnitId: 0,
      quantity: 0,
    });
    setActiveSubstanceSearchTerm("");
    setSelectedActiveSubstance(null);
    setActiveSubstanceSearchResults([]);
    setShowActiveSubstanceDropdown(false);
    setShowAddActiveSubstanceSection(false);
    setError("");
    setSuccess("");
  };

  const handleSubmitActiveSubstance = async () => {
    setError("");
    setSuccess("");

    if (!activeSubstanceFormData.medicationId || !activeSubstanceFormData.activeSubstanceId || 
        !activeSubstanceFormData.activeSubstanceUnitId || activeSubstanceFormData.quantity <= 0) {
      setError("Please fill in all fields with valid values");
      return;
    }

    try {
      const api = new Api();
      if (editingMedicationActiveSubstance) {
        await api.updateMedicationActiveSubstance(editingMedicationActiveSubstance.id, activeSubstanceFormData);
        setSuccess("Medication active substance updated successfully");
      } else {
        await api.createMedicationActiveSubstance(activeSubstanceFormData);
        setSuccess("Medication active substance created successfully");
      }
      handleCancelEditActiveSubstance();
      setShowAddActiveSubstanceSection(false);
      loadData();
    } catch (err: any) {
      setError(err.response?.data?.message || "An error occurred");
    }
  };

    const handleDeleteActiveSubstance = async (id: number) => {
      if (!window.confirm("Are you sure you want to delete this medication active substance?")) {
        return;
      }

      try {
        const api = new Api();
        await api.deleteMedicationActiveSubstance(id);
        setSuccess("Medication active substance deleted successfully");
        loadData();
      } catch (err: any) {
        setError(err.response?.data?.message || "An error occurred");
      }
    };

  if (loading) {
    return <div>Loading...</div>;
  }

  const currentMedicationActiveSubstances = editingMedication 
    ? getMedicationActiveSubstances(editingMedication.id)
    : [];

  return (
    <div>
      <Row className="mb-3">
        <Col>
          <h3>Medications</h3>
        </Col>
        <Col xs="auto">
          <Button variant="primary" onClick={handleOpenCreateMedication}>
            Create New Medication
          </Button>
        </Col>
      </Row>

      {error && <Alert variant="danger" onClose={() => setError("")} dismissible>{error}</Alert>}
      {success && <Alert variant="success" onClose={() => setSuccess("")} dismissible>{success}</Alert>}

      <Table striped bordered hover>
        <thead>
          <tr>
            <th>ID</th>
            <th>Name</th>
            <th>Presentation</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {medications.map((medication) => (
            <tr key={medication.id}>
              <td>{medication.id}</td>
              <td>{medication.name}</td>
              <td>{medication.presentation || "-"}</td>
              <td>
                <Button
                  variant="outline-primary"
                  size="sm"
                  onClick={() => handleOpenEditMedication(medication)}
                  className="me-2"
                >
                  Edit
                </Button>
                <Button
                  variant="outline-danger"
                  size="sm"
                  onClick={() => handleDeleteMedication(medication.id)}
                >
                  Delete
                </Button>
              </td>
            </tr>
          ))}
        </tbody>
      </Table>

      {/* Medication Modal */}
      <Modal show={showMedicationModal} onHide={handleCloseMedicationModal} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            {editingMedication ? "Edit Medication" : "Create Medication"}
          </Modal.Title>
        </Modal.Header>
        <Form id="medication-form" onSubmit={handleSubmitMedication}>
          <Modal.Body>
            {error && <Alert variant="danger">{error}</Alert>}
            {success && <Alert variant="success">{success}</Alert>}
            
            <Form.Group className="mb-3">
              <Form.Label>Name</Form.Label>
              <Form.Control
                type="text"
                value={medicationFormData.name}
                onChange={(e) => setMedicationFormData({ ...medicationFormData, name: e.target.value })}
                required
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Presentation</Form.Label>
              <Form.Select
                value={medicationFormData.presentation}
                onChange={(e) => setMedicationFormData({ ...medicationFormData, presentation: e.target.value })}
                required
              >
                <option value="">Select a presentation</option>
                <option value="tableta">Tableta</option>
                <option value="cápsula">Cápsula</option>
                <option value="gragea">Gragea</option>
                <option value="ampolla">Ampolla</option>
                <option value="frasco ámpula">Frasco ámpula</option>
              </Form.Select>
            </Form.Group>

            {editingMedication && (
              <>
                <hr className="my-4" />
                <h5 className="mb-3">Active Substances</h5>
                
                {currentMedicationActiveSubstances.length > 0 ? (
                  <Table striped bordered hover size="sm" className="mb-4">
                    <thead>
                      <tr>
                        <th>Active Substance</th>
                        <th>Unit</th>
                        <th>Quantity</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {currentMedicationActiveSubstances.map((mas) => (
                        <tr key={mas.id}>
                          <td>
                            {mas.activeSubstance ? mas.activeSubstance.name : `Substance ID: ${mas.activeSubstanceId}`}
                          </td>
                          <td>
                            {mas.activeSubstanceUnit
                              ? `${mas.activeSubstanceUnit.name} (${mas.activeSubstanceUnit.abbreviation})`
                              : `Unit ID: ${mas.activeSubstanceUnitId}`}
                          </td>
                          <td>{mas.quantity % 1 === 0 ? mas.quantity.toString().split('.')[0] : Number(mas.quantity).toFixed(1)}</td>
                          <td>
                            <Button
                              variant="outline-primary"
                              size="sm"
                              onClick={() => handleOpenEditActiveSubstance(mas)}
                              className="me-2"
                            >
                              Edit
                            </Button>
                            <Button
                              variant="outline-danger"
                              size="sm"
                              onClick={() => handleDeleteActiveSubstance(mas.id)}
                            >
                              Delete
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                ) : (
                  <p className="text-muted mb-3">No active substances added yet.</p>
                )}

                <hr className="my-4" />
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h6 className="mb-0">{editingMedicationActiveSubstance ? "Edit Active Substance" : "Add New Active Substance"}</h6>
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    onClick={() => setShowAddActiveSubstanceSection(!showAddActiveSubstanceSection)}
                    aria-expanded={showAddActiveSubstanceSection}
                  >
                    {showAddActiveSubstanceSection ? "Collapse" : "Expand"}
                  </Button>
                </div>
                
                {showAddActiveSubstanceSection && (
                  <div>
                    <Form.Group className="mb-3" style={{ position: "relative" }} data-active-substance-search>
                      <Form.Label>Active Substance {activeSubstanceFormData.activeSubstanceId > 0 && <span className="text-danger">*</span>}</Form.Label>
                      
                      {selectedActiveSubstance ? (
                        <div className="d-flex align-items-center gap-2 mb-2">
                          <Badge bg="primary" style={{ fontSize: "0.9rem", padding: "6px 12px" }}>
                            {selectedActiveSubstance.name}
                          </Badge>
                          <Button
                            variant="link"
                            size="sm"
                            onClick={handleClearActiveSubstance}
                            style={{ padding: 0, textDecoration: "none" }}
                          >
                            <span style={{ fontSize: "1.2rem", color: "#dc3545" }}>×</span>
                          </Button>
                          <Form.Text className="text-muted" style={{ margin: 0 }}>
                            Click × to change
                          </Form.Text>
                        </div>
                      ) : null}
                      
                      {(!selectedActiveSubstance || activeSubstanceSearchTerm) && (
                        <Form.Control
                          type="text"
                          value={activeSubstanceSearchTerm}
                          onChange={(e) => handleActiveSubstanceSearchChange(e.target.value)}
                          placeholder={selectedActiveSubstance ? "Search to change active substance..." : "Search for an active substance..."}
                          required={!selectedActiveSubstance && !editingMedicationActiveSubstance}
                          autoComplete="off"
                        />
                      )}
                      
                      {showActiveSubstanceDropdown && activeSubstanceSearchResults.length > 0 && (
                        <div
                          style={{
                            position: "absolute",
                            top: selectedActiveSubstance ? "calc(100% + 40px)" : "100%",
                            left: 0,
                            right: 0,
                            zIndex: 1000,
                            backgroundColor: "white",
                            border: "1px solid #ced4da",
                            borderTop: "none",
                            borderRadius: "0 0 0.25rem 0.25rem",
                            maxHeight: "200px",
                            overflowY: "auto",
                            boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                          }}
                        >
                          {activeSubstanceSearchResults.map((substance) => (
                            <div
                              key={substance.id}
                              onClick={() => handleSelectActiveSubstance(substance)}
                              style={{
                                padding: "8px 12px",
                                cursor: "pointer",
                                borderBottom: "1px solid #f0f0f0",
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.backgroundColor = "#f8f9fa";
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.backgroundColor = "white";
                              }}
                            >
                              {substance.name}
                            </div>
                          ))}
                        </div>
                      )}
                      
                      {activeSubstanceFormData.activeSubstanceId === 0 && !activeSubstanceSearchTerm && (
                        <Form.Text className="text-muted">
                          {selectedActiveSubstance ? "" : "Please search and select an active substance"}
                        </Form.Text>
                      )}
                    </Form.Group>
                    
                    <Form.Group className="mb-3">
                      <Form.Label>Active Substance Unit</Form.Label>
                      <Form.Select
                        value={activeSubstanceFormData.activeSubstanceUnitId}
                        onChange={(e) => setActiveSubstanceFormData({ ...activeSubstanceFormData, activeSubstanceUnitId: parseInt(e.target.value) })}
                      >
                        <option value={0}>Select a unit</option>
                        {activeSubstanceUnits.map((unit) => (
                          <option key={unit.id} value={unit.id}>
                            {unit.name} ({unit.abbreviation})
                          </option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                    
                    <Form.Group className="mb-3">
                      <Form.Label>Quantity</Form.Label>
                      <Form.Control
                        type="number"
                        min="0.01"
                        step="0.01"
                        value={activeSubstanceFormData.quantity}
                        onChange={(e) => setActiveSubstanceFormData({ ...activeSubstanceFormData, quantity: parseFloat(e.target.value) || 0 })}
                      />
                    </Form.Group>
                    
                    <div className="d-flex gap-2">
                      <Button variant="primary" type="button" onClick={handleSubmitActiveSubstance}>
                        {editingMedicationActiveSubstance ? "Update Active Substance" : "Add Active Substance"}
                      </Button>
                      {editingMedicationActiveSubstance && (
                        <Button variant="secondary" type="button" onClick={handleCancelEditActiveSubstance}>
                          Cancel
                        </Button>
                      )}
                    </div>
                  </div>
                )}
              </>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleCloseMedicationModal}>
              Close
            </Button>
            <Button variant="primary" type="submit" form="medication-form">
              {editingMedication ? "Update Medication" : "Create Medication"}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </div>
  );
};

export default Medications;
